import React, { useEffect, useMemo, useState } from "react";
import { getGares, createGare, updateGare, deleteGare } from "../api/api";
import Table from "../components/Table";
import { Button, Form, Container, Modal, Spinner } from "react-bootstrap";
import { useNotifications } from "../components/NotificationsContext";

export default function GaresList() {
  const { addNotification } = useNotifications();

  const [list, setList] = useState([]);
  const [page, setPage] = useState(1);
  const perPage = 5;

  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [selected, setSelected] = useState(null); // null=create, object=edit
  const [form, setForm] = useState({ nom: "" });

  const columns = useMemo(() => [{ Header: "Nom", accessor: "nom" }], []);

  const load = async () => {
    try {
      const res = await getGares();
      setList(res.data);
    } catch (err) {
      console.error("Erreur chargement gares:", err);
      addNotification({
        type: "error",
        title: "Erreur",
        message: "Impossible de charger la liste des gares.",
        delay: 6000,
      });
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setSelected(null);
    setForm({ nom: "" });
    setErrors({});
    setShowModal(true);
  };

  const openEdit = (row) => {
    setSelected(row);
    setForm({ nom: row?.nom || "" });
    setErrors({});
    setShowModal(true);
  };

  const validate = () => {
    const e = {};
    if (!form.nom.trim()) e.nom = "Nom requis";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      setSaving(true);
      const payload = { nom: form.nom.trim() };

      if (selected?.id) {
        await updateGare(selected.id, payload);
        setList((prev) => prev.map((g) => (g.id === selected.id ? { ...g, ...payload } : g)));

        addNotification({
          type: "success",
          title: "Succès",
          message: "Gare mise à jour!",
          delay: 4000,
        });
      } else {
        const res = await createGare(payload);
        setList((prev) => [...prev, res.data]);

        addNotification({
          type: "success",
          title: "Succès",
          message: "Gare ajoutée avec succès.",
          delay: 4000,
        });
      }

      setShowModal(false);
      setSelected(null);
      setForm({ nom: "" });
      setErrors({});
    } catch (err) {
      console.error("Erreur save gare:", err.response?.data || err);
      setErrors((prev) => ({ ...prev, ...(err?.response?.data || {}) }));

      addNotification({
        type: "error",
        title: "Erreur",
        message: err?.response?.data?.nom?.[0] || err?.response?.data?.detail || "Opération échouée.",
        delay: 7000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!row || !row.id) {
      addNotification({
        type: "warning",
        title: "Attention",
        message: "Aucune donnée pour cette ligne.",
      });
      return;
    }

    if (!window.confirm(`Supprimer ${row.nom} ?`)) return;

    try {
      await deleteGare(row.id);
      setList((prev) => prev.filter((g) => g.id !== row.id));

      addNotification({
        type: "success",
        title: "Suppression",
        message: `Gare "${row.nom}" supprimée.`,
        delay: 4000,
      });
    } catch (err) {
      console.error("Erreur suppression gare:", err.response?.data || err);
      addNotification({
        type: "error",
        title: "Erreur",
        message: "Échec de la suppression.",
        delay: 7000,
      });
    }
  };

  return (
    <Container style={{ maxWidth: 900 }} className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Liste des Gares</h4>
        <Button onClick={openCreate} variant="success">
          ➕ Ajouter
        </Button>
      </div>

      {/* Modal create/edit */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{selected?.id ? "Modifier la gare" : "Ajouter une gare"}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group>
            <Form.Label>Nom *</Form.Label>
            <Form.Control
              placeholder="Nom"
              value={form.nom}
              onChange={(e) => setForm({ nom: e.target.value })}
              isInvalid={!!errors.nom}
            />
            <Form.Control.Feedback type="invalid">
              {Array.isArray(errors.nom) ? errors.nom[0] : errors.nom}
            </Form.Control.Feedback>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)} disabled={saving}>
            Annuler
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? <Spinner size="sm" className="me-2" /> : null}
            Enregistrer
          </Button>
        </Modal.Footer>
      </Modal>

      <Table
        data={list}
        columns={columns}
        onEdit={openEdit}
        onDelete={handleDelete}
        currentPage={page}
        setPage={setPage}
        perPage={perPage}
      />
    </Container>
  );
}
